import { useTranslation } from 'react-i18next'

import { InvoicesContract } from '@/Invoices/application'
import { InvoiceModel } from '@/Invoices/domain'
import { InvoicesRepository } from '@/Invoices/domain/InvoicesRepository'
import {
  FilterResultModel,
  HttpModel,
  PaginationModel,
  SearchParamsModel,
  dataToExcel,
  edenredProducts,
  textToPdf,
} from '@/Shared/domain'
import { useBackendTable } from '@/Shared/ui/Table'
import { useLoader } from '@/Shared/ui/loader'

import { invoicesTranslation } from '../invoicesTranslation'
import { Item } from './useBatchCheckboxes'

export interface IUseInvoices {
  invoices?: InvoiceModel[]
  loadInvoices: () => Promise<boolean>
  downloadPdf: (creditNoteId: number, invoiceNumber: string) => Promise<void>
  downloadAllPdfs: () => Promise<void>
  downloadTransactions: (
    creditNoteId: number,
    invoiceNumber: string,
    productType: edenredProducts.ticketRestaurant | edenredProducts.ticketGuarderia,
  ) => Promise<void>
  downloadAllTransactions: (
    // invoicesIds: number[],
    productType: edenredProducts.ticketRestaurant | edenredProducts.ticketGuarderia,
  ) => Promise<void>
  numberOfRecordsPerPage: number
  page: number
  count: number
  onPageChange: (page: number) => void
  search: (search: string, filters?: FilterResultModel[]) => void
  searchParameters: SearchParamsModel
}

export const useInvoices = (
  repository: InvoicesRepository,
  resultsPerPage: number,
): IUseInvoices => {
  const { startLoading, stopLoading } = useLoader()
  const { t } = useTranslation()

  const load = async (
    page: number,
    itemsPerPage: number,
    searchParameters: SearchParamsModel,
  ): Promise<HttpModel<PaginationModel<InvoiceModel>>> => {
    return InvoicesContract(repository).get(
      page,
      itemsPerPage,
      searchParameters.search,
      searchParameters.filters,
    )
  }

  const { items, loadItems, itemsPerPage, ...rest } = useBackendTable<InvoiceModel>(
    load,
    undefined,
    resultsPerPage,
  )

  const downloadPdf = async (
    creditNoteId: number,
    invoiceNumber: string,
  ): Promise<void> => {
    startLoading()

    const response = await InvoicesContract(repository).downloadPdf([creditNoteId])

    stopLoading()

    if (response.data && response.data[0])
      textToPdf(
        response.data[0].file,
        response.data[0].fileName ? response.data[0].fileName : invoiceNumber,
      )
  }

  const downloadAllPdfs = async (): Promise<void> => {
    startLoading()

    const storedActiveInvoices = sessionStorage.getItem('activeInvoices')
    const activeInvoices: Item[] = storedActiveInvoices
      ? JSON.parse(storedActiveInvoices)
      : []
    const invoicesIds = activeInvoices
      .filter(s => s.hasInvoiceAvailable && s.isInvoiceDownloadeable)
      .map(s => s.id)

    const response = await InvoicesContract(repository).downloadPdf(invoicesIds)
    stopLoading()

    if (response.data) {
      Promise.all(
        response.data.map(async file => {
          return textToPdf(file.file, file?.fileName ?? invoicesIds.toString())
        }),
      ).catch(error => {
        console.error('Error downloading PDFs:', error)
      })
    }
  }

  const downloadTransactions = async (
    creditNoteId: number,
    invoiceNumber: string,
    productType: edenredProducts.ticketRestaurant | edenredProducts.ticketGuarderia,
  ): Promise<void> => {
    startLoading()

    const response = await InvoicesContract(repository).downloadTransactions(
      [creditNoteId],
      productType,
    )

    stopLoading()

    if (response.data)
      dataToExcel(
        response.data.file,
        response.data.fileName ? response.data.fileName : invoiceNumber,
      )
  }

  const downloadAllTransactions = async (
    productType: edenredProducts.ticketRestaurant | edenredProducts.ticketGuarderia,
  ): Promise<void> => {
    startLoading()

    const storedActiveInvoices = sessionStorage.getItem('activeInvoices')
    const activeInvoices: Item[] = storedActiveInvoices
      ? JSON.parse(storedActiveInvoices)
      : []

    const invoicesIds = activeInvoices
      .filter(s => s.hasTransactionAvailable)
      .map(s => s.id)

    const response = await InvoicesContract(repository).downloadTransactions(
      invoicesIds,
      productType,
    )

    stopLoading()

    if (response.data)
      dataToExcel(
        response.data.file,
        response.data.fileName
          ? response.data.fileName
          : t(invoicesTranslation.toolbar.transactionsDefaultName),
      )
  }

  return {
    loadInvoices: loadItems,
    downloadPdf,
    downloadAllPdfs,
    downloadTransactions,
    downloadAllTransactions,
    invoices: items,
    numberOfRecordsPerPage: itemsPerPage,
    ...rest,
  }
}
